import { Card } from "@material-tailwind/react";
import { CiMenuKebab } from "react-icons/ci";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { FiFilter } from "react-icons/fi";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";

enum AuditAction {
  PDD_TEMPLATE_CREATE = "PDD_TEMPLATE_CREATE",
  PDD_TEMPLATE_DELETE = "PDD_TEMPLATE_DELETE",
  PDD_CREATE = "PDD_CREATE",
  PDD_UPDATE = "PDD_UPDATE",
  USER_CREATE = "USER_CREATE",
  USER_UPDATE = "USER_UPDATE",
  USER_DELETE = "USER_DELETE",
}

import JsonView from "@uiw/react-json-view";

const DetailModal = ({
  isOpen,
  onClose,
  details,
}: {
  isOpen: boolean;
  onClose: () => void;
  details: any;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-50 w-full max-w-3xl rounded-lg bg-white shadow-xl">
        <div className="max-h-[80vh] overflow-y-auto p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-medium">Request Details</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
          <JsonView
            value={details}
            style={{
              background: "white",
              padding: "1rem",
              borderRadius: "0.375rem",
            }}
            displayObjectSize={false}
            displayDataTypes={false}
            enableClipboard
          />
        </div>
      </div>
    </div>
  );
};
const AuditTrail = () => {
  const [perPage] = useState(10);
  const [filters, setFilters] = useState({
    user: "",
    action: "",
    startDate: "",
    endDate: "",
  });
  const { ref, inView } = useInView();

  const [selectedDetails, setSelectedDetails] = useState<any>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [isActionDropdownOpen, setIsActionDropdownOpen] = useState(false);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["auditLogs", filters, selectedActions],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams({
        page: pageParam.toString(),
        per_page: perPage.toString(),
        ...(filters.user && { user: filters.user }),
        ...(selectedActions.length > 0 && { action: selectedActions.join(",") }),
        ...(filters.startDate && { start_date: filters.startDate }),
        ...(filters.endDate && { end_date: filters.endDate }),
      });

      const response = await api.get(`/audit-log/?${params.toString()}`);
      return response.data;
    },
    getNextPageParam: (lastPage, pages) => {
      const nextPage = pages.length + 1;
      return lastPage.data.length === perPage ? nextPage : undefined;
    },
    initialPageParam: 1,
  });
  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage]);

  const allLogs = data?.pages.flatMap((page) => page.data) ?? [];
  const totalLogs = data?.pages[0]?.total || 0;

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  const {
    data: usersData,
    fetchNextPage: fetchNextUsers,
    hasNextPage: hasMoreUsers,
    isFetchingNextPage: isLoadingMoreUsers,
  } = useInfiniteQuery({
    queryKey: ["users", filters.user],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await api.get("/users/list", {
        params: {
          search: filters.user || "",
          page: pageParam,
          page_size: 10,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage, pages) => {
      const nextPage = pages.length + 1;
      return lastPage.data.length === 10 ? nextPage : undefined;
    },
    initialPageParam: 1,
    enabled: isUserDropdownOpen, // Only fetch when dropdown is open
  });

  const TABLE_HEAD = ["Action", "User", "Status", "Details", "New Values", "Time & Date", ""];

  return (
    <>
      <section className="space-y-4 px-4">
        <div className="w-full rounded-md bg-white p-4 shadow-sm">
          <div>
            <h1 className="font-semibold">Total Audit Logs</h1>
            <p className="text-gray-700">{totalLogs}</p>
          </div>
        </div>
        <div className="rounded-md bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-500" />
            <span className="text-sm font-medium">Filters</span>
          </div>
          <div className="my-2 flex flex-wrap items-center gap-4">
            <div className="relative min-w-[200px]">
              <label className="text-xs text-gray-600">User</label>

              <input
                type="text"
                placeholder="Filter by user"
                value={filters.user}
                onChange={(e) => setFilters((prev) => ({ ...prev, user: e.target.value }))}
                onFocus={() => setIsUserDropdownOpen(true)}
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-[#714ECE] focus:outline-none"
              />
              {isUserDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsUserDropdownOpen(false)}
                  />
                  <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white shadow-lg">
                    {usersData?.pages.map((page) =>
                      page.data.map((user: any) => (
                        <div
                          key={user.id}
                          className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                          onClick={() => {
                            setFilters((prev) => ({ ...prev, user: user.name || user.email }));
                            setIsUserDropdownOpen(false);
                          }}
                        >
                          {user.name || user.email}
                        </div>
                      )),
                    )}
                    {hasMoreUsers && (
                      <div
                        className="cursor-pointer px-4 py-2 text-center text-sm text-gray-600 hover:bg-gray-100"
                        onClick={() => fetchNextUsers()}
                      >
                        {isLoadingMoreUsers ? "Loading more..." : "Load more"}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="relative min-w-[300px]">
              <label className="text-xs text-gray-600">Action</label>
              <div
                onClick={() => setIsActionDropdownOpen(!isActionDropdownOpen)}
                className="flex cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-sm focus:border-[#714ECE]"
              >
                <div className="flex flex-wrap gap-1">
                  {selectedActions.length > 0
                    ? selectedActions.map((action) => (
                        <span
                          key={action}
                          className="rounded-md bg-[#714ECE]/10 px-2 py-0.5 text-[#714ECE]"
                        >
                          {action}
                        </span>
                      ))
                    : "Select Actions"}
                </div>
                <svg
                  className={`h-5 w-5 transform transition-transform ${
                    isActionDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {isActionDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsActionDropdownOpen(false)}
                  />
                  <div className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow-lg">
                    {Object.values(AuditAction).map((action) => (
                      <div
                        key={action}
                        className="flex cursor-pointer items-center px-4 py-2 hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedActions((prev) =>
                            prev.includes(action)
                              ? prev.filter((a) => a !== action)
                              : [...prev, action],
                          );
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedActions.includes(action)}
                          onChange={() => {}}
                          className="mr-2 h-4 w-4 rounded border-gray-300 text-[#714ECE]"
                        />
                        <span>{action}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
                className="rounded-md border px-3 py-2 text-sm focus:border-[#714ECE] focus:outline-none"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
                className="rounded-md border px-3 py-2 text-sm focus:border-[#714ECE] focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-600">‎ </label>
              <button
                onClick={() => setFilters({ user: "", action: "", startDate: "", endDate: "" })}
                className="rounded-md px-4 py-2 text-sm text-[#714ECE] hover:bg-[#714ECE]/10"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
        <div
          style={{ maxWidth: `calc(100vw - 320px)` }}
          className="rounded-md bg-white p-4 shadow-sm"
        >
          <Card placeholder={""} className="h-full w-full overflow-auto">
            <table className="w-full min-w-max table-auto text-left">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head) => (
                    <th key={head} className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                      <span className="text-sm font-medium text-gray-700">{head}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-sm">
                {allLogs.map((log, index) => {
                  const isLast = index === allLogs.length - 1;
                  const classes = isLast ? "p-4" : "p-4 border-b border-blue-gray-50";

                  return (
                    <tr key={log.id}>
                      <td className={classes}>
                        <span className="text-[#714ECE]">{log.action}</span>
                      </td>
                      <td className={classes}>
                        <div className="flex flex-col">
                          <span className="font-medium">{log.user.email || ""}</span>
                          <span className="text-xs text-gray-500">
                            {log.user.role.replaceAll("_", " ")}
                          </span>
                        </div>
                      </td>
                      <td className={classes}>
                        <span>{log.status}</span>
                      </td>
                      <td
                        className={`${classes}  cursor-pointer  hover:text-[#714ECE]`}
                        onClick={() => {
                          if (log.details) {
                            setSelectedDetails(JSON.parse(log.details));
                            setIsDetailModalOpen(true);
                          }
                        }}
                      >
                        <span className="border-black-500 rounded-md border-2 px-2 py-1">
                          {log.details ? "View Details" : "-"}
                        </span>
                      </td>
                      <td className={classes}>
                        <span>
                          {log.new_values
                            ? JSON.stringify(log.new_values).slice(0, 50) + "..."
                            : "-"}
                        </span>
                      </td>
                      <td className={classes}>
                        <span>{formatDateTime(log.timestamp)}</span>
                      </td>
                      <td className={classes}>
                        <CiMenuKebab />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div ref={ref} className="p-4 text-center">
              {isFetchingNextPage ? (
                <span>Loading more...</span>
              ) : hasNextPage ? (
                <span>Load more</span>
              ) : (
                <span>No more results</span>
              )}
            </div>
          </Card>
        </div>
      </section>
      {selectedDetails && (
        <DetailModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          details={selectedDetails}
        />
      )}
    </>
  );
};

export default AuditTrail;
